import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, Video, FileText, HelpCircle, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { PageHeader, Field } from "../../components/common";

const COURSES = [["yellow-belt", "Yellow Belt"], ["green-belt", "Green Belt"], ["black-belt", "Black Belt"]];
const blankQ = () => ({ q: "", options: ["", "", "", ""], answer: 0 });

const QuizEditor = ({ quiz, setQuiz }) => (
  <div className="space-y-3" data-testid="quiz-editor">
    {quiz.map((q, i) => (
      <div key={i} className="rounded-md border p-3 space-y-2">
        <div className="flex gap-2">
          <input className="dx-input" placeholder={`Question ${i + 1}`} value={q.q} onChange={(e) => setQuiz(quiz.map((x, k) => (k === i ? { ...x, q: e.target.value } : x)))} data-testid={`qe-q${i}`} />
          <button onClick={() => setQuiz(quiz.filter((_, k) => k !== i))} aria-label="Remove question" data-testid={`qe-remove-${i}`}><Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {q.options.map((o, j) => (
            <label key={j} className="flex items-center gap-2">
              <input type="radio" checked={q.answer === j} onChange={() => setQuiz(quiz.map((x, k) => (k === i ? { ...x, answer: j } : x)))} data-testid={`qe-q${i}-correct${j}`} />
              <input className="dx-input py-1.5" placeholder={`Option ${j + 1}`} value={o} onChange={(e) => setQuiz(quiz.map((x, k) => (k === i ? { ...x, options: x.options.map((y, m) => (m === j ? e.target.value : y)) } : x)))} data-testid={`qe-q${i}-opt${j}`} />
            </label>
          ))}
        </div>
      </div>
    ))}
    <button className="dx-btn-ghost py-1.5" onClick={() => setQuiz([...quiz, blankQ()])} data-testid="qe-add-btn"><Plus className="w-4 h-4" />Add question</button>
  </div>
);

const PartCheckEditor = ({ modId, part, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [c, setC] = useState(part.check ? { ...part.check, options: [...part.check.options, "", "", "", ""].slice(0, 4) } : { ...blankQ(), explain: "" });
  const opt = (j, v) => setC({ ...c, options: c.options.map((o, m) => (m === j ? v : o)) });
  const save = async () => {
    const options = c.options.map((o) => o.trim());
    const filled = options.filter(Boolean);
    if (!c.q.trim() || filled.length < 2 || !options[c.answer]) return toast.error("Add a question, at least 2 options and tick the correct one");
    const answer = filled.indexOf(options[c.answer]);
    try { await api.put(`/admin/modules/${modId}/parts/${part.id}/check`, { q: c.q, options: filled, answer, explain: c.explain }); toast.success("Question saved"); setOpen(false); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const clear = async () => { await api.delete(`/admin/modules/${modId}/parts/${part.id}/check`); onChanged(); };
  return (
    <div className="pl-7">
      <button className="text-xs font-semibold text-[#5B2A6E] inline-flex items-center gap-1" onClick={() => setOpen(!open)} data-testid={`me-part-check-toggle-${part.id}`}>
        <HelpCircle className="w-3.5 h-3.5" />{part.check ? `Question: ${part.check.q}` : "Add a quick question"}
      </button>
      {open && (
        <div className="mt-2 rounded-md border bg-white p-3 space-y-2" data-testid={`me-part-check-editor-${part.id}`}>
          <input className="dx-input" placeholder="Question" value={c.q} onChange={(e) => setC({ ...c, q: e.target.value })} data-testid="pc-q" />
          <div className="grid sm:grid-cols-2 gap-2">
            {c.options.map((o, j) => (
              <label key={j} className="flex items-center gap-2">
                <input type="radio" checked={c.answer === j} onChange={() => setC({ ...c, answer: j })} data-testid={`pc-correct${j}`} />
                <input className="dx-input py-1.5" placeholder={`Option ${j + 1}`} value={o} onChange={(e) => opt(j, e.target.value)} data-testid={`pc-opt${j}`} />
              </label>
            ))}
          </div>
          <input className="dx-input" placeholder="Short explanation shown after answering" value={c.explain} onChange={(e) => setC({ ...c, explain: e.target.value })} data-testid="pc-explain" />
          <div className="flex justify-between">
            {part.check ? <button className="text-xs text-rose-600 font-semibold" onClick={clear} data-testid="pc-remove-btn">Remove question</button> : <span />}
            <button className="dx-btn-gold py-1.5" onClick={save} data-testid="pc-save-btn"><Save className="w-4 h-4" />Save question</button>
          </div>
        </div>
      )}
    </div>
  );
};

const ModuleEditor = ({ mod, onChanged }) => {
  const [f, setF] = useState({ title: mod.title, content: mod.content, order: mod.order, quiz: mod.quiz || [] });
  const [partTitle, setPartTitle] = useState("");
  const [busy, setBusy] = useState("");
  const save = async () => {
    const quiz = f.quiz.map((q) => ({ ...q, options: q.options.filter((o) => o.trim()) })).filter((q) => q.q.trim() && q.options.length >= 2);
    try { await api.put(`/admin/modules/${mod.id}`, { ...f, order: Number(f.order) || 0, quiz }); toast.success("Module saved"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (kind === "parts") fd.append("title", partTitle || `Part ${(mod.parts?.length || 0) + 1}`);
    setBusy(kind);
    try { await api.post(`/admin/modules/${mod.id}/${kind}`, fd); toast.success("Uploaded"); setPartTitle(""); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const delPart = async (pid) => { await api.delete(`/admin/modules/${mod.id}/parts/${pid}`); onChanged(); };
  const delModule = async () => { if (window.confirm("Delete this module?")) { await api.delete(`/admin/modules/${mod.id}`); onChanged(); } };

  return (
    <div className="dx-card p-6 space-y-5" data-testid={`module-editor-${mod.id}`}>
      <div className="grid sm:grid-cols-6 gap-3">
        <div className="sm:col-span-5"><Field label="Module title"><input className="dx-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="me-title" /></Field></div>
        <Field label="Order"><input type="number" className="dx-input" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} data-testid="me-order" /></Field>
      </div>
      <Field label="Lesson notes"><textarea rows={6} className="dx-input" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="me-content" /></Field>
      <div>
        <div className="dx-label">Video parts (aim for 5-7 minutes each)</div>
        <div className="space-y-2">
          {(mod.parts || []).map((p) => (
            <div key={p.id} className="text-sm rounded-md bg-[#FAF8FC] px-3 py-2 space-y-1" data-testid={`me-part-${p.id}`}>
              <div className="flex items-center gap-3">
                <Video className="w-4 h-4 text-[#5B2A6E]" /><span className="font-semibold">{p.title}</span><span className="text-xs text-[#8a7a92] truncate flex-1">{p.filename}</span>
                <button onClick={() => delPart(p.id)} aria-label="Remove part"><Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" /></button>
              </div>
              <PartCheckEditor key={JSON.stringify(p.check || {})} modId={mod.id} part={p} onChanged={onChanged} />
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <input className="dx-input sm:w-56" placeholder="Part title (optional)" value={partTitle} onChange={(e) => setPartTitle(e.target.value)} data-testid="me-part-title" />
          <label className="dx-btn-ghost cursor-pointer" data-testid="me-upload-part">
            <Upload className="w-4 h-4" />{busy === "parts" ? "Uploading…" : "Upload video part"}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" disabled={!!busy} onChange={(e) => upload("parts", e.target.files?.[0])} />
          </label>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="dx-label mb-0">Slides</div>
        {mod.slides && <span className="text-sm flex items-center gap-1"><FileText className="w-4 h-4 text-[#5B2A6E]" />{mod.slides.filename}</span>}
        <label className="dx-btn-ghost cursor-pointer py-1.5" data-testid="me-upload-slides">
          <Upload className="w-4 h-4" />{busy === "slides" ? "Uploading…" : mod.slides ? "Replace PDF" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" disabled={!!busy} onChange={(e) => upload("slides", e.target.files?.[0])} />
        </label>
      </div>
      <div>
        <div className="dx-label">Session quiz (students must score 60% to complete the module)</div>
        <QuizEditor quiz={f.quiz} setQuiz={(quiz) => setF({ ...f, quiz })} />
      </div>
      <div className="flex justify-between border-t pt-4">
        <button className="text-sm text-rose-600 font-semibold" onClick={delModule} data-testid="me-delete-module">Delete module</button>
        <button className="dx-btn-gold" onClick={save} data-testid="me-save-btn"><Save className="w-4 h-4" />Save module</button>
      </div>
    </div>
  );
};

export default function CourseContent() {
  const [slug, setSlug] = useState("black-belt");
  const [mods, setMods] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get(`/admin/courses/${slug}/modules`).then((r) => { setMods(r.data); setSel((s) => (r.data.find((m) => m.id === s) ? s : r.data[0]?.id)); });
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    const { data } = await api.post(`/admin/courses/${slug}/modules`, { title: "New session", content: "", order: mods.length, quiz: [] });
    setSel(data.id); load();
  };
  const mod = mods.find((m) => m.id === sel);

  return (
    <div>
      <PageHeader eyebrow="Academy" title="Course content" subtitle="Add sessions, upload short video parts and slides, and set a quiz students must pass after each session." />
      <div className="flex flex-wrap gap-2 mb-5">
        {COURSES.map(([s, l]) => <button key={s} onClick={() => setSlug(s)} className={s === slug ? "dx-btn-purple" : "dx-btn-ghost"} data-testid={`cc-course-${s}`}>{l}</button>)}
        <Link to={`/app/learn/${slug}?module=${Math.max(mods.findIndex((m) => m.id === sel), 0)}`} className="dx-btn-gold sm:ml-auto" data-testid="cc-preview-btn"><Eye className="w-4 h-4" />Preview as learner</Link>
      </div>
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4 dx-card p-3 h-fit">
          {mods.map((m) => (
            <button key={m.id} onClick={() => setSel(m.id)} data-testid={`cc-module-${m.id}`} className={`w-full text-left rounded-md px-3 py-2.5 text-sm ${m.id === sel ? "bg-[#F4EEF7] font-semibold" : "hover:bg-[#FAF8FC]"}`}>
              {m.order + 1}. {m.title}<div className="text-xs text-[#8a7a92] font-normal">{m.parts?.length || 0} video parts · {m.quiz?.length || 0} quiz questions</div>
            </button>
          ))}
          <button className="dx-btn-gold w-full mt-2" onClick={add} data-testid="cc-add-module-btn"><Plus className="w-4 h-4" />Add session</button>
        </div>
        <div className="lg:col-span-8">{mod && <ModuleEditor key={mod.id + (mod.parts?.length || 0) + (mod.slides?.path || "")} mod={mod} onChanged={load} />}</div>
      </div>
    </div>
  );
}
